/**
 * This file holds all actions related to checkout and creates a
 * reducer to be imported into the CheckoutContext
 */

import {
  calcCartSubtotal,
  calcCartTotal,
  getChargeSuppressingTrialDays,
} from "../shared/calcStateItems"
import { State, Action, CheckoutActionKind } from "../types/checkoutTypes"
import {
  addToCart,
  decrementCartItem,
  incrementCartItem,
  removeFromCart,
} from "./actions"
import { CheckoutSteps } from "./constants"

const updateTotals = (state: State): State => {
  const trialDays = getChargeSuppressingTrialDays(state)
  const total = calcCartTotal(
    state.cartItems,
    state.currency,
    trialDays,
    state.taxes,
    state.shipping,
    state.couponDiscount
  )
  const subTotal = calcCartSubtotal(state.cartItems, state.currency, trialDays)

  if (state.promptAddToCart.length > 0) {
    state.promptAddToCart = state.promptAddToCart.filter(
      p => !state.cartItems.some(i => i.productId === p.productId)
    )
  }

  return { ...state, total, subTotal }
}
export const checkoutReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case CheckoutActionKind.GO_TO_STEP: {
      const initCheckout =
        !state.isChecoutInitiated &&
        action.payload.stepNumber >= CheckoutSteps.Shipping

      return {
        ...state,
        currentStep: action.payload.stepNumber,
        isChecoutInitiated: state.isChecoutInitiated || initCheckout,
      }
    }
    case CheckoutActionKind.SET_SHIPPING: {
      return updateTotals({ ...state, shipping: action.payload.amount })
    }
    case CheckoutActionKind.SET_TAXES: {
      return updateTotals({ ...state, taxes: action.payload.amount })
    }
    case CheckoutActionKind.SET_SHIPPING_NOTES: {
      return { ...state, shippingNotes: action.payload.notes }
    }
    case CheckoutActionKind.ADD_TO_CART: {
      return updateTotals(addToCart(action, state))
    }
    case CheckoutActionKind.PROMPT_ADD_TO_CART: {
      return {
        ...state,
        promptAddToCart: [...state.promptAddToCart, action.payload],
      }
    }
    case CheckoutActionKind.CLEAR_PROMPT_ADD_TO_CART: {
      return {
        ...state,
        promptAddToCart: [],
      }
    }
    case CheckoutActionKind.REMOVE_FROM_CART: {
      return updateTotals(removeFromCart(action, state))
    }
    case CheckoutActionKind.INCREMENT_CART_ITEM: {
      return updateTotals(incrementCartItem(action, state))
    }
    case CheckoutActionKind.DECREMENT_CART_ITEM: {
      return updateTotals(decrementCartItem(action, state))
    }
    case CheckoutActionKind.CLEAR_CART: {
      return {
        ...state,
        cartItems: [],
        shipping: undefined,
        taxes: undefined,
        couponDiscount: 0,
        couponCode: "",
        couponDescription: "",
        total: 0,
        subTotal: 0,
        isGift: false,
        promptAddToCart: [],
      }
    }
    case CheckoutActionKind.CLEAR_COUPONS: {
      return updateTotals({
        ...state,
        couponDiscount: 0,
        couponDiscountDays: 0,
        couponCode: "",
        couponDescription: "",
      })
    }
    case CheckoutActionKind.SET_COUNTRY_INVENTORY: {
      return { ...state, countryInventory: action.payload.country }
    }
    case CheckoutActionKind.SET_CURRENCY: {
      const { currency } = action.payload

      return updateTotals({
        ...state,
        currency,
      })
    }

    case CheckoutActionKind.SET_COUPON_DISCOUNT: {
      return updateTotals({ ...state, couponDiscount: action.payload.amount })
    }
    case CheckoutActionKind.SET_COUPON_DISCOUNT_DAYS: {
      return updateTotals({ ...state, couponDiscountDays: action.payload.days })
    }
    case CheckoutActionKind.SET_GRANTS_LINKED_USER_MEMBERSHIP_TRIAL_DAYS: {
      // The server applies this grant as the new subscription's trial, so like a coupon
      // trial it has to keep the subscription out of what is charged today.
      return updateTotals({
        ...state,
        grantsLinkedUserMembershipTrialDays: action.payload.days,
      })
    }
    case CheckoutActionKind.SET_COUPON_CODE: {
      return { ...state, couponCode: action.payload.code }
    }
    case CheckoutActionKind.SET_COUPON_DESCRIPTION: {
      return { ...state, couponDescription: action.payload.description }
    }
    case CheckoutActionKind.TOGGLE_GIFT_ORDER: {
      return { ...state, isGift: !state.isGift }
    }
    case CheckoutActionKind.SET_CONTRACT_ACCEPTED_TERMS: {
      return {
        ...state,
        contractAcceptedTerms: action.payload.detailedTerms,
      }
    }
    default: {
      return state
    }
  }
}
